import { useRef } from "react";
import { deepCopy } from "../helpers";

export type PathMap = Record<string, string[]>;

export interface BuildResult {
  expression: string; // expr with paths replaced by /root/nodeX
  xml: string;        // <root><node0>...</node0>...</root>
  nodeNameByPath: Record<string, string>; // maps original path -> nodeX
}

export default class CodeHelper {
  private pathMap: PathMap = {};

  /** Get a deep copy of current path map */
  public getPathMap = () => deepCopy(this.pathMap);

  /** Replace current pathMap entirely (deep copy) */
  public setPathMap = (next: PathMap) => {
    this.pathMap = deepCopy(next);
  }

  public updatePathMap = (key: string, index: number, value: string) => {
    this.pathMap[key][index] = value;
    return this.getPathMap();
  }

  public removePathMapValue = (key: string, index: number) => {
    this.pathMap[key].splice(index, 1);
    return this.getPathMap();
  }

  public formatCode = (input: string) => {
    let str = this.collapseCode(input);
    
    let formattedStr = "";
    let indentlevel = 0;
    let isNewLine = true;
    let isString = false;
    let isEscaped = false;
    
    const indent = (level) => {
      let r = "";
      for (let i = 0; i < level; i++) {
        r += "\t";
      }
      return r;      
    };

    const newline = () => {
      isNewLine = true;
      return "\r\n";
    };
        
    for (let i = 0; i < str.length; i++) {
      let char = str[i];
      
      // string handling interrupt
      if (isString) {
        
        if (char == "\\") {
          isEscaped = !isEscaped;
        } else if (char == "'") {
          if (isEscaped) isEscaped = false;
          else isString = !isString;
        } else {
          isEscaped = false;          
        }
          
        formattedStr += char;
        continue;
      } else if (char == "'") {        
        isString = true;
        
        if (isNewLine) formattedStr += indent(indentlevel); 
        
        formattedStr += char;
        isNewLine = false;
        continue;
      }            
      
      // formatting before placing character
      switch (char) {
        case "(":
          if (!isNewLine) formattedStr += newline();    
          break;
        case ")":
          indentlevel--;
          
          if (!isNewLine) formattedStr += newline();                                 
          break;
        case " ":
          if (isNewLine) continue;
          break;                     
      }
      
      if (isNewLine) formattedStr += indent(indentlevel); 
      
      formattedStr += char;
      isNewLine = false;
      
      // formatting after placing character
      switch (char) {
        case "(":
          formattedStr += newline();
          indentlevel++;
          break;        
        case ",":
          formattedStr += ' ';
          formattedStr += newline();
          break;
      }
    }
    
    return formattedStr;
  };
  
  public collapseCode = (str: string) => {
    let rx1 = /[\r\n\t]+/g;
    let rx2 = /\s+/g;
    return (str ?? "").replace(rx1, "").replace(rx2, " ").trim();
  };
  
  public buildEvaluationPayload = (expression: string) => {  
    // get keys and sort by longest to shortest
    let keys = Object.keys(this.pathMap).sort((a, b) => b.length - a.length);
    
    // create xml document and replace expression xpaths
    let expr = this.collapseCode(expression);
    let xml = "<root>";
    
    for (let i = 0; i < keys.length; i++) {
      let key = keys[i];
      let values = this.pathMap[key];
      
      let nodeName = "node" + i.toString();
      let xpath = "/root/" + nodeName;
      
      for(let k = 0; k < values.length; k++) {
        let val = values[k];
        
        xml += "<" + nodeName + ">" + val + "</" + nodeName + ">";
      }
      
      // @ts-ignore replaceAll works i promise
      expr = expr.replaceAll(key, xpath);
    }
    
    xml += "</root>";
    
    return { expression: expr, xml } as BuildResult;
  };
  
  public updatePathMapFromExpression = (expression: string) => {
    // parse the expression
    let expr = this.collapseCode(expression);
    
    let isPath = false;
    let predicateDepth = 0;
    let isString = false;
    let isEscaped = false;
    
    let pathBuffer = "";
    let paths: PathMap = {};
    
    for (let i = 0; i < expr.length; i++) {
      let char = expr[i];
      
      if (isString) {
        switch (char) {
          case "\\":
            isEscaped = !isEscaped;
            break;
          case "'":
            if (isEscaped) isEscaped = false;
            else isString = false;            
            break;
          default:
            isEscaped = false;
        }
      } else if (isPath && predicateDepth > 0) {
        switch(char)
        {
          case "[":
            predicateDepth++;
            break;
          case "]":
            predicateDepth--;
            break;
        }
        pathBuffer += char;
      } else if (isPath) {
        switch (char) {
          case " ":
          case ")":
          case "(":
          case ",":
            isPath = false;
            paths[pathBuffer] = [""];
            pathBuffer = "";
            break;
          case "[":
            predicateDepth++;
            pathBuffer += char;
            break;
          default:
            pathBuffer += char;
        }
      } else {
        switch(char) {
          case "'":
            isString = true;
            break;
          case "/":
          case ".":
          case "@":
          case "#":
            isPath = true;
            pathBuffer += char;
            break;
        }
      }
    }
    
    if (isPath) paths[pathBuffer] = [""];
    
    // replace paths, but keep existing values 
    for(let i in paths) {
      if (this.pathMap[i]) paths[i] = this.pathMap[i];
    }
    this.pathMap = paths;
    return this.pathMap;
  };
}

export function useCodeHelper() {
  const ref = useRef<CodeHelper>();
  if (!ref.current) ref.current = new CodeHelper();
  return ref.current;
}
